import { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';

import {
  Button,
  ButtonText,
  ContractIcon,
  ExpandIcon,
  Lightbox,
  OverlayTriggerTooltip,
  TableData,
} from '@utilities';

import { OPTION_LABEL_ACCESSORS } from './utilities/constants';

import {
  getColumnAccessor,
  getDisplayText,
  getOptionLabel,
  getQuestionOptionId,
  getThumbnailRegionId,
  parseOptionId,
} from './utilities/helpers';

import styles from './_index.module.scss';

const OptionImageTable = ({
  actionButtons = [],
  columns = [],
  data = [],
  questionOptions = [],
}) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [expandedOptionIds, setExpandedOptionIds] = useState(new Set());
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);

  const { lightboxImages, optionImageMap } = useMemo(() => {
    const map = new Map();
    const images = [];

    questionOptions.forEach((option, optionIndex) => {
      const optionId = getQuestionOptionId(option);
      const imageUrl = option?.image?.cdnUrl;

      if (optionId === null || !imageUrl || map.has(optionId)) return;

      const label = getOptionLabel({
        fallbackLabel: `Option ${optionIndex + 1}`,
        option,
      });
      const image = {
        alt: label,
        caption: label,
        src: imageUrl,
      };

      map.set(optionId, {
        ...image,
        imageIndex: images.length,
      });
      images.push(image);
    });

    return { lightboxImages: images, optionImageMap: map };
  }, [questionOptions]);

  const availableOptionIds = useMemo(() => {
    const uniqueOptionIds = new Set();

    data.forEach(({ option_id: optionId }) => {
      const parsedOptionId = parseOptionId(optionId);
      if (parsedOptionId === null || !optionImageMap.has(parsedOptionId)) return;
      uniqueOptionIds.add(parsedOptionId);
    });

    return Array.from(uniqueOptionIds);
  }, [data, optionImageMap]);

  const isAllExpanded = useMemo(() => {
    if (!availableOptionIds.length) return false;
    return availableOptionIds.every((optionId) => expandedOptionIds.has(optionId));
  }, [availableOptionIds, expandedOptionIds]);

  useEffect(() => {
    const availableOptionIdsSet = new Set(availableOptionIds);

    setExpandedOptionIds((previousState) => {
      return new Set(
        Array.from(previousState).filter((optionId) => availableOptionIdsSet.has(optionId))
      );
    });
  }, [availableOptionIds]);

  const stopEvent = useCallback((event) => {
    event.stopPropagation();
  }, []);

  const onOpenLightbox = useCallback(
    (optionId) => {
      const option = optionImageMap.get(optionId);
      if (!option) return;

      setCurrentImageIndex(option.imageIndex);
      setIsLightboxOpen(true);
    },
    [optionImageMap]
  );

  const onToggleAllOptionImages = () => {
    setExpandedOptionIds(isAllExpanded ? new Set() : new Set(availableOptionIds));
  };

  const onToggleOptionImage = useCallback((optionId) => {
    setExpandedOptionIds((previousState) => {
      const updatedState = new Set(previousState);

      if (updatedState.has(optionId)) {
        updatedState.delete(optionId);
      } else {
        updatedState.add(optionId);
      }

      return updatedState;
    });
  }, []);

  const optionLabelAccessor = useMemo(() => {
    return columns
      .map((column) => getColumnAccessor(column))
      .find((accessor) => OPTION_LABEL_ACCESSORS.includes(accessor));
  }, [columns]);

  const tableColumns = useMemo(() => {
    if (!optionLabelAccessor || optionImageMap.size === 0) return columns;

    return columns.map((column) => {
      const accessor = getColumnAccessor(column);
      if (accessor !== optionLabelAccessor) return column;

      const originalCell = column.Cell;

      return {
        ...column,
        Cell: (cellProps) => {
          const labelText = getDisplayText(cellProps?.value);
          const optionId = parseOptionId(cellProps?.row?.original?.option_id);
          const optionImage = optionId === null ? null : optionImageMap.get(optionId);

          if (!optionImage) {
            return originalCell ? originalCell(cellProps) : labelText;
          }

          const isExpanded = expandedOptionIds.has(optionId);
          const thumbnailRegionId = getThumbnailRegionId({
            optionId,
            rowId: cellProps?.row?.id ?? cellProps?.row?.index ?? optionId,
          });
          const toggleLabel = `${isExpanded ? 'Hide' : 'Show'} image for ${labelText}`;

          return (
            <div className={styles['option-image-cell']}>
              <ButtonText
                aria-controls={thumbnailRegionId}
                aria-expanded={isExpanded}
                aria-label={toggleLabel}
                className={styles['option-image-toggle']}
                onClick={(event) => {
                  event.stopPropagation();
                  onToggleOptionImage(optionId);
                }}
                onMouseDown={stopEvent}
                type="button"
              >
                {labelText}
              </ButtonText>
              {isExpanded && (
                <div
                  aria-label={`Image preview for ${labelText}`}
                  className={styles['option-image-thumbnail-region']}
                  id={thumbnailRegionId}
                  role="region"
                >
                  <button
                    aria-label={`Open image for ${labelText}`}
                    className={styles['option-image-thumbnail-button']}
                    onClick={(event) => {
                      event.stopPropagation();
                      onOpenLightbox(optionId);
                    }}
                    onMouseDown={stopEvent}
                    type="button"
                  >
                    <img
                      alt={optionImage.alt}
                      className={styles['option-image-thumbnail']}
                      src={optionImage.src}
                    />
                  </button>
                </div>
              )}
            </div>
          );
        },
        meta: {
          ...(column.meta || {}),
          allowCellWrap: true,
          isOptionLabel: true,
        },
      };
    });
  }, [
    columns,
    expandedOptionIds,
    onOpenLightbox,
    onToggleOptionImage,
    optionImageMap,
    optionLabelAccessor,
    stopEvent,
  ]);

  const hasOptionImages = availableOptionIds.length > 0;
  const toggleImagesButtonText = isAllExpanded ? 'Hide All Images' : 'Show All Images';

  const tableActionButtons = hasOptionImages
    ? [
        ...actionButtons,
        <OverlayTriggerTooltip content={toggleImagesButtonText} key="option-image-expand-toggle">
          <Button
            aria-label={toggleImagesButtonText}
            className={styles['option-image-table-toggle-button']}
            icon={
              isAllExpanded ? (
                <ContractIcon className={styles['option-image-table-toggle-icon']} />
              ) : (
                <ExpandIcon className={styles['option-image-table-toggle-icon']} />
              )
            }
            onClick={onToggleAllOptionImages}
            text={toggleImagesButtonText}
            variant="secondary"
          />
        </OverlayTriggerTooltip>,
      ]
    : actionButtons;

  return (
    <>
      <TableData actionButtons={tableActionButtons} columns={tableColumns} data={data} />
      {lightboxImages.length > 0 && (
        <Lightbox
          currentImageIndex={currentImageIndex}
          images={lightboxImages}
          isActive={isLightboxOpen}
          onClose={() => setIsLightboxOpen(false)}
        />
      )}
    </>
  );
};

OptionImageTable.propTypes = {
  actionButtons: PropTypes.arrayOf(PropTypes.node),
  columns: PropTypes.arrayOf(PropTypes.object),
  data: PropTypes.arrayOf(PropTypes.object),
  questionOptions: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      image: PropTypes.shape({
        cdnUrl: PropTypes.string,
      }),
      optionSourceId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      text: PropTypes.string,
      title: PropTypes.string,
      value: PropTypes.string,
    })
  ),
};

export default OptionImageTable;
